import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { getApiUrl } from '../lib/config';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'pending'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const pending = searchParams.get('pending') === 'true';
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token');

  // If this is a pending verification (user just signed up or logged in with unverified email)
  if (pending) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface">
        <div className="max-w-md w-full card p-8 text-center space-y-6">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-tertiary/10 border border-tertiary/20">
            <span className="material-icons text-tertiary text-3xl">mark_emailunread</span>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-display font-bold text-on-surface">
              Verify your email
            </h2>
            <p className="text-sm text-on-surface-variant/70">
              {email ? (
                <>
                  We sent a verification link to{' '}
                  <strong className="text-on-surface">{email}</strong>.
                </>
              ) : (
                'We sent a verification link to your email address.'
              )}
              <br />
              Please check your inbox and spam folder to activate your account.
            </p>
          </div>

          {resendError && (
            <div className="alert alert-error py-2 text-sm">
              <span className="material-icons text-sm">error</span>
              {resendError}
            </div>
          )}

          {sent && !resendError && (
            <div className="alert alert-success py-2 text-sm">
              <span className="material-icons text-sm">check_circle</span>
              Verification email sent! Check your inbox.
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={async () => {
                setSending(true);
                setResendError(null);
                try {
                  const emailToUse = email || undefined;
                  const { error: sendError } = await (authClient as any).sendVerificationEmail({
                    ...(emailToUse ? { email: emailToUse } : {}),
                    callbackURL: window.location.origin + '/auth/verify-email',
                  });
                  if (sendError) {
                    setResendError(sendError.message || 'Failed to send verification email.');
                  } else {
                    setSent(true);
                  }
                } catch (err: any) {
                  setResendError(err?.message || 'Something went wrong.');
                } finally {
                  setSending(false);
                }
              }}
              disabled={sending}
              className="btn btn-primary w-full gap-2"
            >
              <span className="material-icons text-sm">
                {sending ? 'hourglass_empty' : 'refresh'}
              </span>
              {sending ? 'Sending...' : sent ? 'Resend verification email' : 'Resend verification email'}
            </button>

            <Link
              to="/login"
              className="btn btn-ghost w-full"
            >
              Go to Login
            </Link>
          </div>

          <p className="text-xs text-on-surface-variant/40 mt-4">
            Once verified, you'll be able to access your dashboard.
          </p>
        </div>
      </div>
    );
  }

  // Token verification flow (user clicked the link in their email)
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token found. Please request a new verification email.');
      return;
    }

    async function verifyEmail() {
      try {
        const response = await fetch(
          `${getApiUrl('/api/auth/verify-email')}?token=${encodeURIComponent(token!)}`,
          { method: 'GET', credentials: 'include' }
        );

        if (response.ok) {
          setStatus('success');
          // Refresh the session to pick up the updated emailVerified status
          await authClient.getSession({ fetchOptions: { forceRefetch: true, credentials: 'include' as RequestCredentials } });
          // Auto-redirect after short delay
          setTimeout(() => {
            navigate('/home', { replace: true });
          }, 3000);
        } else {
          const data = await response.json().catch(() => ({}));
          setStatus('error');
          setErrorMessage(data?.message || 'Email verification failed. The link may have expired.');
        }
      } catch (err: any) {
        setStatus('error');
        setErrorMessage(err?.message || 'Something went wrong during verification.');
      }
    }

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-surface">
      <div className="max-w-md w-full card p-8 text-center space-y-4">
        {status === 'loading' && (
          <>
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-tertiary/10 border border-tertiary/20 animate-pulse">
              <span className="material-icons text-tertiary">sync</span>
            </div>
            <h2 className="text-xl font-display font-bold text-on-surface">
              Verifying your email...
            </h2>
            <p className="text-sm text-on-surface-variant/50">
              Please wait while we verify your email address.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <span className="material-icons text-emerald-400">check_circle</span>
            </div>
            <h2 className="text-xl font-display font-bold text-on-surface">
              Email Verified!
            </h2>
            <p className="text-sm text-on-surface-variant/50">
              Your email has been verified successfully. Redirecting to your dashboard...
            </p>
            <div className="w-48 mx-auto h-1 bg-outline-variant/10 overflow-hidden">
              <div className="h-full bg-emerald-400/60 animate-[progress_1.5s_ease-in-out_forwards]" />
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-red-500/10 border border-red-500/20">
              <span className="material-icons text-red-400">close</span>
            </div>
            <h2 className="text-xl font-display font-bold text-on-surface">
              Verification Failed
            </h2>
            <p className="text-sm text-on-surface-variant/50">{errorMessage}</p>
            <div className="flex gap-3 justify-center mt-6">
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="btn btn-primary btn-sm"
              >
                Go to Login
              </button>
              <button
                onClick={() => navigate('/auth/verify-email?pending=true', { replace: true })}
                className="btn btn-ghost btn-sm"
              >
                Resend Verification
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;